// Google Analytics 4 Integration
// Replace VITE_GA_MEASUREMENT_ID in your .env file with your actual GA4 measurement ID

declare global {
    interface Window {
        gtag: (...args: unknown[]) => void;
        dataLayer: unknown[];
    }
}

const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID?.trim();
const analyticsDebug = import.meta.env.DEV && new URLSearchParams(window.location.search).has('analytics-debug');
const analyticsEnabled = Boolean(
    analyticsDebug || (GA_MEASUREMENT_ID && /^G-[A-Z0-9]+$/i.test(GA_MEASUREMENT_ID) && GA_MEASUREMENT_ID !== 'G-XXXXXXXXXX')
);

export type AnalyticsConsent = 'granted' | 'denied' | null;
const CONSENT_STORAGE_KEY = 'nurvo-analytics-consent';
let analyticsInitialized = false;
let lastPageViewPath: string | null = null;

export const getAnalyticsConsent = (): AnalyticsConsent => {
    const value = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    return value === 'granted' || value === 'denied' ? value : null;
};

export const setAnalyticsConsent = (consent: Exclude<AnalyticsConsent, null>) => {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, consent);

    if (consent === 'granted') {
        if (analyticsInitialized && window.gtag) {
            window.gtag('consent', 'update', { analytics_storage: 'granted' });
        } else {
            initGA();
        }
        return;
    }

    if (window.gtag) {
        window.gtag('consent', 'update', { analytics_storage: 'denied' });
    }

    document.cookie.split(';').forEach((cookie) => {
        const name = cookie.split('=')[0]?.trim();
        if (name?.startsWith('_ga')) {
            document.cookie = `${name}=; Max-Age=0; path=/; SameSite=Lax`;
            document.cookie = `${name}=; Max-Age=0; path=/; domain=.${window.location.hostname}; SameSite=Lax`;
        }
    });
};

// Initialize Google Analytics
export const initGA = () => {
    if (!analyticsEnabled || analyticsInitialized || getAnalyticsConsent() !== 'granted') return;

    analyticsInitialized = true;
    if (analyticsDebug) document.documentElement.dataset.analyticsReady = 'true';

    // Add GA script to head
    if (!analyticsDebug && GA_MEASUREMENT_ID) {
        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
        document.head.appendChild(script);
    }

    // Initialize dataLayer and gtag
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag(...args: unknown[]) {
        window.dataLayer.push(args);
    };
    window.gtag('js', new Date());
    window.gtag('config', GA_MEASUREMENT_ID || 'G-LOCALDEBUG', {
        send_page_view: true
    });
};

// Track page views
export const trackPageView = (path: string, title?: string) => {
    if (!analyticsEnabled || !window.gtag || lastPageViewPath === path) return;
    lastPageViewPath = path;

    window.gtag('event', 'page_view', {
        page_path: path,
        page_title: title || document.title
    });
};

// Track custom events
export const trackEvent = (
    eventName: string,
    params?: Record<string, string | number | boolean>
) => {
    if (!analyticsEnabled || !window.gtag) return;

    const payload = { page_path: window.location.pathname, ...params };
    window.gtag('event', eventName, payload);
    if (analyticsDebug) {
        document.documentElement.dataset.analyticsEvent = eventName;
        document.documentElement.dataset.analyticsPayload = JSON.stringify(payload);
        document.documentElement.dataset.analyticsCount = String(Number(document.documentElement.dataset.analyticsCount || 0) + 1);
    }
};

// Common events for Nurvo
export const analytics = {
    // Navigation events
    navClick: (item: string) => trackEvent('nav_click', { item }),

    // CTA events
    ctaClick: (location: string, buttonText: string) =>
        trackEvent('cta_click', { location, button_text: buttonText }),

    // App download events
    appStoreClick: (location: string, buttonText: string) =>
        trackEvent('app_store_click', { store: 'ios', location, button_text: buttonText }),

    // Newsletter signup
    newsletterSignup: (location: string) =>
        trackEvent('newsletter_signup', { location }),

    // Pricing events
    pricingToggle: (plan: 'monthly' | 'yearly') =>
        trackEvent('pricing_toggle', { plan }),

    pricingClick: (plan: string, price: string) =>
        trackEvent('pricing_click', { plan, price }),

    // Feature exploration
    featureView: (feature: string) =>
        trackEvent('feature_view', { feature }),

    // FAQ interaction
    faqExpand: (question: string) =>
        trackEvent('faq_expand', { question }),

    // Video events
    videoPlay: (videoId?: string) =>
        trackEvent('video_play', { video_id: videoId || 'demo' }),

    videoComplete: (videoId?: string) =>
        trackEvent('video_complete', { video_id: videoId || 'demo' }),

    // Contact form
    contactFormStart: () => trackEvent('contact_form_start'),
    contactFormSubmit: (subject: string) =>
        trackEvent('contact_form_submit', { subject }),

    // Theme toggle
    themeChange: (theme: 'dark' | 'light') =>
        trackEvent('theme_change', { theme }),

    // Scroll depth
    scrollDepth: (percentage: number) =>
        trackEvent('scroll_depth', { percentage }),
};

export default analytics;
